import React, { useState, useCallback } from 'react'
import AppDropdown from './AppDropdown'
import AppCombobox from './AppCombobox'
import AppDateInput from './AppDateInput'
import AppActionMenu from './AppActionMenu'
import { appToast } from './AppToast'
import {
  fetchCompanyOptions as defaultFetchCompanyOptions,
  fetchBranchOptions as defaultFetchBranchOptions,
  fetchDepartmentOptions as defaultFetchDepartmentOptions,
  fetchUserOptions as defaultFetchUserOptions,
  fetchProjectOptions as defaultFetchProjectOptions,
  fetchTaskOptions as defaultFetchTaskOptions,
  fetchJobOptions as defaultFetchJobOptions,
  fetchEmployeeOptions as defaultFetchEmployeeOptions,
} from '../api/filterOptionApi'
import { PROJECT_TYPE_OPTIONS } from '../features/project-management/project/constants/projectConstants'

export default function SharedFilters({
  // Filter values
  searchField = 'Name',
  searchText = '',
  statusFilter = 'Active',
  companyFilter = 'All',
  branchFilter = 'All',
  departmentFilter = 'All',
  placeFilter = 'All',
  groupFilter = 'All',
  empGroupFilter,
  genderFilter = 'All',
  headTypeFilter = 'All',
  fromDate = '',
  toDate = '',
  tillDate = '',

  // New specific values
  bankNameFilter = '',
  agentFilter = 'All',
  customerFilter = 'All',
  vendorFilter = 'All',
  filterFilter = 'All',

  // Inventory specific values
  typeFilter = 'All',
  valClassFilter = 'All',
  stockTypeFilter = 'As per book',
  deptStockFilter = false,

  // Education specific values
  moduleFilter = 'All',
  courseFilter = 'All',
  schoolFilter = 'All',
  facultyFilter = 'All',
  categoryFilter = 'All',
  academicYearFilter = 'All',
  periodFilter = 'Period',
  batchFilter = 'All',
  subjectFilter = 'All',
  dayFilter = 'All',

  // Project Management specific values
  projectFilter = 'All',
  taskFilter = 'All',
  jobFilter = 'All',
  leadFilter = 'All',
  priorityFilter = 'All',
  billingModeFilter = 'All',
  slaStateFilter = 'All',
  overrunFilter = 'All',
  portfolioFilter = 'active',
  projectTypeFilter = 'all',

  // Payroll & Compensation specific values
  datePreset = '',
  datePresetFilter,
  setDatePreset,
  setDatePresetFilter,
  datePresetOptions = [],
  fromEmpId = '',
  setFromEmpId,
  tillEmpId = '',
  setTillEmpId,
  fromAmount = '',
  setFromAmount,
  tillAmount = '',
  setTillAmount,
  payHeadFilter = 'All',
  setPayHeadFilter,
  payHeadOptions = [],
  empIdFilter = '',
  setEmpIdFilter,
  amountFilter = '',
  setAmountFilter,
  employeeFilter = '',
  setEmployeeFilter,
  employeeOptions = [],
  reasonFilter = 'All',
  setReasonFilter,
  reasonOptions = [],
  monthFilter,
  setMonthFilter,
  monthOptions = [],
  yearFilter,
  setYearFilter,
  yearOptions = [],
  weeklyOffFilter = 'Select',
  setWeeklyOffFilter,
  weeklyOffOptions = [],
  secondWeeklyOffFilter = 'Select',
  setSecondWeeklyOffFilter,
  secondWeeklyOffOptions = [],

  // Setters / Handlers
  setSearchField,
  setSearchText,
  setStatusFilter,
  setPortfolioFilter,
  setCompanyFilter,
  setBranchFilter,
  setDepartmentFilter,
  setPlaceFilter,
  setGroupFilter,
  setEmpGroupFilter,
  setGenderFilter,
  setHeadTypeFilter,
  setFromDate,
  setToDate,
  setTillDate,

  // New specific setters
  setBankNameFilter,
  setAgentFilter,
  setCustomerFilter,
  setVendorFilter,
  setFilterFilter,

  // Inventory specific setters
  setTypeFilter,
  setValClassFilter,
  setStockTypeFilter,
  setDeptStockFilter,

  // Education specific setters
  setModuleFilter,
  setCourseFilter,
  setSchoolFilter,
  setFacultyFilter,
  setCategoryFilter,
  setAcademicYearFilter,
  setPeriodFilter,
  setBatchFilter,
  setSubjectFilter,
  setDayFilter,

  onReset,
  onSearch,

  // Dropdown options
  searchFieldOptions = [],
  statusOptions = [],
  attendanceStatusOptions = [],
  companyOptions = [],
  branchOptions = [],
  departmentOptions = [],
  placeOptions = [],
  groupOptions = [],
  empGroupOptions = [],
  genderOptions = [],
  headTypeOptions = [],

  // New specific options
  agentOptions = [],
  customerOptions = [],
  vendorOptions = [],
  filterOptions = [],

  // Inventory specific options
  typeOptions = [],
  valClassOptions = [],
  stockTypeOptions = [],

  // Education specific options
  moduleOptions = [],
  courseOptions = [],
  schoolOptions = [],
  facultyOptions = [],
  categoryOptions = [],
  academicYearOptions = [],
  periodOptions = [],
  batchOptions = [],
  subjectOptions = [],
  dayOptions = [],

  // Project Management specific setters
  setProjectFilter,
  setTaskFilter,
  setJobFilter,
  setLeadFilter,
  setPriorityFilter,
  setBillingModeFilter,
  setSlaStateFilter,
  setOverrunFilter,
  setProjectTypeFilter,

  // Project Management specific options
  projectOptions = [],
  taskOptions = [],
  jobOptions = [],
  leadOptions = [],
  priorityOptions = [],
  billingModeOptions = [],
  slaStateOptions = [],
  overrunOptions = [],
  portfolioOptions = [],
  projectTypeOptions = [],

  // Async filter option fetchers
  fetchCompanyOptions,
  fetchBranchOptions,
  fetchDepartmentOptions,
  fetchAgentOptions,
  fetchProjectOptions,
  fetchTaskOptions,
  fetchJobOptions,
  fetchEmployeeOptions,

  // Which filters to show in the accordion
  accordionFilters,
  visibleAccordionFilters = [
    'company',
    'branch',
    'department',
    'place',
    'group',
    'headType',
    'fromDate',
    'toDate',
  ],

  // Show / hide main controls
  showSearch = true,
  showStatus = true,
  showAccordionToggle = true,

  // Custom action callbacks
  tools = [],
  onToolSelect,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const activeFilters = accordionFilters || visibleAccordionFilters

  const handleFetchCompany = useCallback(
    (search, page, pageSize) => (fetchCompanyOptions || defaultFetchCompanyOptions)(search, page, pageSize),
    [fetchCompanyOptions],
  )

  const handleFetchBranch = useCallback(
    (search, page, pageSize) => (fetchBranchOptions || defaultFetchBranchOptions)(search, page, pageSize, companyFilter),
    [fetchBranchOptions, companyFilter],
  )

  const handleFetchDepartment = useCallback(
    (search, page, pageSize) =>
      (fetchDepartmentOptions || defaultFetchDepartmentOptions)(search, page, pageSize, companyFilter, branchFilter),
    [fetchDepartmentOptions, companyFilter, branchFilter],
  )

  const handleFetchAgent = useCallback(
    (search, page, pageSize) => (fetchAgentOptions || defaultFetchUserOptions)(search, page, pageSize),
    [fetchAgentOptions],
  )

  const handleFetchProject = useCallback(
    (search, page, pageSize) => (fetchProjectOptions || defaultFetchProjectOptions)(search, page, pageSize),
    [fetchProjectOptions],
  )

  const handleFetchTask = useCallback(
    (search, page, pageSize) => (fetchTaskOptions || defaultFetchTaskOptions)(search, page, pageSize, projectFilter),
    [fetchTaskOptions, projectFilter],
  )

  const handleFetchJob = useCallback(
    (search, page, pageSize) => (fetchJobOptions || defaultFetchJobOptions)(search, page, pageSize, projectFilter),
    [fetchJobOptions, projectFilter],
  )

  const handleFetchEmployee = useCallback(
    (search, page, pageSize) => (fetchEmployeeOptions || defaultFetchEmployeeOptions)(search, page, pageSize),
    [fetchEmployeeOptions],
  )

  const effectiveGroupFilter = empGroupFilter !== undefined ? empGroupFilter : groupFilter
  const effectiveSetGroupFilter = setEmpGroupFilter || setGroupFilter
  const effectiveGroupOptions = empGroupOptions.length > 0 ? empGroupOptions : groupOptions

  // Calculate active filters (ignoring 'All', 'all', and empty values)
  const getActiveFilterCount = () => {
    let count = 0
    const isSet = (val) => val && String(val).trim() !== '' && String(val).toLowerCase() !== 'all'
    if (activeFilters.includes('portfolio') && isSet(portfolioFilter) && String(portfolioFilter).toLowerCase() !== 'active') count++
    if (activeFilters.includes('company') && isSet(companyFilter)) count++
    if (activeFilters.includes('branch') && isSet(branchFilter)) count++
    if (activeFilters.includes('department') && isSet(departmentFilter)) count++
    if (activeFilters.includes('employee') && isSet(employeeFilter)) count++
    if (activeFilters.includes('reason') && isSet(reasonFilter)) count++
    if (activeFilters.includes('datePreset') && isSet(datePreset || datePresetFilter)) count++
    if (activeFilters.includes('place') && isSet(placeFilter)) count++
    if (activeFilters.includes('school') && isSet(schoolFilter)) count++
    if ((activeFilters.includes('group') || activeFilters.includes('empGroup')) && isSet(effectiveGroupFilter)) count++
    if (activeFilters.includes('gender') && isSet(genderFilter)) count++
    if (activeFilters.includes('headType') && isSet(headTypeFilter)) count++
    if (activeFilters.includes('fromDate') && fromDate) count++
    if (activeFilters.includes('toDate') && toDate) count++

    // Count specific active filters
    if (activeFilters.includes('bankName') && bankNameFilter) count++
    if ((activeFilters.includes('agent') || Boolean(setAgentFilter)) && isSet(agentFilter)) count++
    if (activeFilters.includes('customer') && isSet(customerFilter)) count++
    if (activeFilters.includes('vendor') && isSet(vendorFilter)) count++
    if (activeFilters.includes('filter') && isSet(filterFilter)) count++

    // Education active filters
    if (activeFilters.includes('module') && isSet(moduleFilter) && moduleFilter !== 'Select Modules') count++
    if (activeFilters.includes('type') && isSet(typeFilter) && typeFilter !== '0') count++
    if (activeFilters.includes('course') && isSet(courseFilter)) count++
    if (activeFilters.includes('faculty') && isSet(facultyFilter)) count++
    if (activeFilters.includes('category') && isSet(categoryFilter)) count++
    if (activeFilters.includes('academicYear') && isSet(academicYearFilter)) count++
    if (activeFilters.includes('period') && isSet(periodFilter) && periodFilter !== 'Period') count++
    if (activeFilters.includes('batch') && isSet(batchFilter)) count++
    if (activeFilters.includes('subject') && isSet(subjectFilter)) count++

    // Project Management active filters
    if (activeFilters.includes('project') && isSet(projectFilter)) count++
    if (activeFilters.includes('task') && isSet(taskFilter)) count++
    if (activeFilters.includes('job') && isSet(jobFilter)) count++
    if (activeFilters.includes('lead') && isSet(leadFilter)) count++
    if (activeFilters.includes('priority') && isSet(priorityFilter)) count++
    if (activeFilters.includes('billingMode') && isSet(billingModeFilter)) count++
    if (activeFilters.includes('projectType') && isSet(projectTypeFilter) && String(projectTypeFilter).toLowerCase() !== 'all') count++
    if ((activeFilters.includes('status') || activeFilters.includes('attendanceStatus')) && isSet(statusFilter) && String(statusFilter).toLowerCase() !== 'all' && String(statusFilter).toLowerCase() !== 'active') count++
    if (activeFilters.includes('slaState') && isSet(slaStateFilter)) count++
    if (activeFilters.includes('overrun') && isSet(overrunFilter)) count++
    if ((activeFilters.includes('status') || showStatus) && isSet(statusFilter) && String(statusFilter).toLowerCase() !== 'active') count++

    // Payroll & Compensation active filters
    if (activeFilters.includes('datePreset') && isSet(datePreset)) count++
    if (activeFilters.includes('payHead') && isSet(payHeadFilter)) count++
    if ((activeFilters.includes('empIdRange') || activeFilters.includes('empId')) && (fromEmpId || tillEmpId || empIdFilter)) count++
    if ((activeFilters.includes('amountRange') || activeFilters.includes('amount')) && (fromAmount || tillAmount || amountFilter)) count++
    if ((activeFilters.includes('monthYear') || activeFilters.includes('month')) && isSet(monthFilter)) count++
    if ((activeFilters.includes('monthYear') || activeFilters.includes('year')) && isSet(yearFilter)) count++
    if (activeFilters.includes('weeklyOff') && isSet(weeklyOffFilter) && weeklyOffFilter !== 'Select' && weeklyOffFilter !== 'All') count++
    if (activeFilters.includes('secondWeeklyOff') && isSet(secondWeeklyOffFilter) && secondWeeklyOffFilter !== 'Select' && secondWeeklyOffFilter !== 'All') count++

    return count
  }

  const activeCount = getActiveFilterCount()

  return (
    <section className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-4 shadow-xs">
      {/* Primary Row (Always Visible) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 w-full">
        {/* Left side: Search & filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1 min-w-0 w-full">
          {/* Merged Connected Search Input Group Wrapper */}
          {showSearch && (
            <div className="flex items-center gap-2 w-full sm:flex-1 sm:max-w-xl">
              {/* Standalone field selector */}
              {searchFieldOptions.length > 0 && (
                <AppDropdown
                  options={searchFieldOptions}
                  value={searchField}
                  onChange={setSearchField}
                  className="shrink-0 w-28 sm:w-36"
                  triggerClassName="h-10 bg-surface-container-lowest text-xs font-bold"
                />
              )}

              {/* Search Input Area with Icon */}
              <div className="flex-1 min-w-0 flex items-center rounded-xl border border-outline-variant/30 bg-surface-container-lowest overflow-hidden transition-all focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary/40 shadow-xs">
                <span className="material-symbols-outlined pl-3.5 text-on-surface-variant text-base pointer-events-none select-none shrink-0">
                  search
                </span>
                <input
                  type="text"
                  value={searchText || ''}
                  onChange={(e) => setSearchText?.(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && onSearch) onSearch()
                  }}
                  placeholder={`Search by ${(searchField || '').toLowerCase()}...`}
                  className="h-10 w-full bg-transparent pl-2.5 pr-4 text-xs text-on-surface outline-none placeholder:text-on-surface-variant border-0 min-w-0"
                />
              </div>
            </div>
          )}

          {/* Action buttons row */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto sm:ml-auto">
            {/* Status Dropdown (Primary row, placed before Users) */}
            {showStatus && statusOptions.length > 0 && (
              <div className="w-full sm:w-36 shrink-0">
                <AppDropdown
                  options={statusOptions}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  triggerClassName="h-10 bg-surface-container-lowest text-xs font-bold w-full"
                />
              </div>
            )}

            {/* Users / Agent Combobox (Primary row, placed directly after Status) */}
            {(activeFilters.includes('agent') || Boolean(setAgentFilter)) && (
              <div className="w-full sm:w-44 shrink-0">
                <AppCombobox
                  fetchOptions={agentOptions.length > 0 ? undefined : handleFetchAgent}
                  options={agentOptions}
                  value={agentFilter === 'All' || agentFilter === 'all' ? '' : agentFilter}
                  onChange={(val) => setAgentFilter?.(val || 'All')}
                  placeholder="All Users"
                  searchPlaceholder="Search user..."
                  triggerClassName="h-10 bg-surface-container-lowest text-xs font-bold w-full"
                />
              </div>
            )}

            {/* Accordion Toggle Button */}
            {showAccordionToggle && activeFilters.length > 0 && (
              <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="h-10 px-3.5 sm:px-4 bg-primary/10 hover:bg-primary/20 text-primary font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 sm:gap-2 cursor-pointer transition select-none flex-1 sm:flex-initial"
              >
                <span className="material-symbols-outlined text-base">tune</span>
                <span>Filters</span>
                {activeCount > 0 && (
                  <span className="bg-primary text-on-primary text-[10px] font-black px-1.5 py-0.2 rounded-full">
                    {activeCount}
                  </span>
                )}
                <span
                  className="material-symbols-outlined text-sm transition-transform duration-200"
                  style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  expand_more
                </span>
              </button>
            )}

            {/* Reset Button */}
            {onReset && (
              <button
                type="button"
                onClick={onReset}
                title="Reset filters"
                className="h-10 px-3 bg-surface-container-highest hover:bg-outline-variant/40 text-on-surface-variant hover:text-on-surface font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 cursor-pointer transition select-none shrink-0"
              >
                <span className="material-symbols-outlined text-base">restart_alt</span>
                <span className="hidden sm:inline">Reset</span>
              </button>
            )}




            {/* Tools dropdown button */}
            {tools && tools.length > 0 && (
              <div className="flex items-center sm:pl-3 sm:border-l sm:border-outline-variant/25 flex-1 sm:flex-initial select-none">
                <AppActionMenu
                  triggerLabel="Tools"
                  triggerIcon="settings"
                  showChevron={true}
                  align="left"
                  triggerClassName="h-10 px-3.5 sm:px-4 bg-surface-container-highest hover:bg-outline-variant/50 text-on-surface-variant font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 sm:gap-2 cursor-pointer transition select-none w-full sm:w-auto"
                  items={tools}
                  onSelect={(key) => {
                    if (onToolSelect) {
                      onToolSelect(key);
                    } else {
                      const item = tools.find(t => t.key === key);
                      const label = item ? item.label : key;
                      appToast.info(`Executing tool: ${label}...`);
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden Accordion Content (Slides Down When Open) */}
      {isOpen && activeFilters.length > 0 && (
        <div className="pt-4 mt-3 border-t border-outline-variant/20 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5 text-xs animate-in fade-in duration-200">
          {activeFilters.includes('portfolio') && portfolioOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Portfolio</span>
              <AppDropdown
                options={portfolioOptions}
                value={portfolioFilter}
                onChange={setPortfolioFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('projectType') && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Project Hierarchy</span>
              <AppDropdown
                options={projectTypeOptions && projectTypeOptions.length > 0 ? projectTypeOptions : PROJECT_TYPE_OPTIONS}
                value={projectTypeFilter}
                onChange={setProjectTypeFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs font-medium"
              />
            </label>
          )}

          {activeFilters.includes('status') && statusOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Status</span>
              <AppDropdown
                options={statusOptions}
                value={statusFilter}
                onChange={setStatusFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('company') && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Company</span>
              <AppCombobox
                fetchOptions={companyOptions.length > 0 ? undefined : handleFetchCompany}
                options={companyOptions}
                value={companyFilter === 'All' || companyFilter === 'all' ? '' : companyFilter}
                onChange={(val) => setCompanyFilter?.(val || 'All')}
                placeholder="All Companies"
                searchPlaceholder="Search company..."
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('branch') && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Branch</span>
              <AppCombobox
                fetchOptions={branchOptions.length > 0 ? undefined : handleFetchBranch}
                options={branchOptions}
                value={branchFilter === 'All' || branchFilter === 'all' ? '' : branchFilter}
                onChange={(val) => setBranchFilter?.(val || 'All')}
                placeholder="All Branches"
                searchPlaceholder="Search branch..."
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('department') && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Department</span>
              <AppCombobox
                fetchOptions={departmentOptions.length > 0 ? undefined : handleFetchDepartment}
                options={departmentOptions}
                value={departmentFilter === 'All' || departmentFilter === 'all' ? '' : departmentFilter}
                onChange={(val) => setDepartmentFilter?.(val || 'All')}
                placeholder="All Departments"
                searchPlaceholder="Search department..."
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('place') && placeOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Place</span>
              <AppDropdown
                options={placeOptions}
                value={placeFilter}
                onChange={setPlaceFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {(activeFilters.includes('group') || activeFilters.includes('empGroup')) && effectiveGroupOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Group</span>
              <AppDropdown
                options={effectiveGroupOptions}
                value={effectiveGroupFilter}
                onChange={effectiveSetGroupFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('gender') && genderOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Gender</span>
              <AppDropdown
                options={genderOptions}
                value={genderFilter}
                onChange={setGenderFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {(activeFilters.includes('attendanceStatus') || (activeFilters.includes('status') && !showStatus)) && (statusOptions.length > 0 || attendanceStatusOptions.length > 0) && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Attendance Status</span>
              <AppDropdown
                options={statusOptions.length > 0 ? statusOptions : attendanceStatusOptions}
                value={statusFilter}
                onChange={setStatusFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('headType') && headTypeOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Filter Type</span>
              <AppDropdown
                options={headTypeOptions}
                value={headTypeFilter}
                onChange={setHeadTypeFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('bankName') && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Bank Name</span>
              <input
                type="text"
                value={bankNameFilter}
                onChange={(e) => setBankNameFilter(e.target.value)}
                placeholder="Bank Name..."
                className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs text-on-surface outline-none placeholder:text-on-surface-variant focus:ring-2 focus:ring-primary/20"
              />
            </label>
          )}



          {activeFilters.includes('customer') && customerOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Customer</span>
              <AppDropdown
                options={customerOptions}
                value={customerFilter}
                onChange={setCustomerFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('vendor') && vendorOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Vendor</span>
              <AppDropdown
                options={vendorOptions}
                value={vendorFilter}
                onChange={setVendorFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('filter') && filterOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Filter</span>
              <AppDropdown
                options={filterOptions}
                value={filterFilter}
                onChange={setFilterFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('datePreset') && datePresetOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Date Period</span>
              <AppDropdown
                options={datePresetOptions}
                value={datePreset || datePresetFilter || ''}
                onChange={(val) => {
                  setDatePreset?.(val);
                  setDatePresetFilter?.(val);
                }}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs font-medium"
              />
            </label>
          )}

          {activeFilters.includes('employee') && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Employee</span>
              <AppCombobox
                fetchOptions={employeeOptions.length > 0 ? undefined : handleFetchEmployee}
                options={employeeOptions}
                value={employeeFilter === 'All' || employeeFilter === 'all' ? '' : employeeFilter}
                onChange={(val) => setEmployeeFilter?.(val || 'All')}
                placeholder="All Employees"
                searchPlaceholder="Search employee..."
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('reason') && reasonOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Reason</span>
              <AppDropdown
                options={reasonOptions}
                value={reasonFilter}
                onChange={setReasonFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs font-medium"
              />
            </label>
          )}

          {(activeFilters.includes('fromDate') || activeFilters.includes('dateRange')) && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">From Date</span>
              <AppDateInput
                value={fromDate}
                onChange={(val) => setFromDate?.(val)}
                placeholder="dd-mm-yyyy"
                inputClassName="h-9 text-xs"
              />
            </label>
          )}

          {(activeFilters.includes('toDate') || activeFilters.includes('tillDate') || activeFilters.includes('dateRange')) && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
                {activeFilters.includes('tillDate') ? 'Till Date' : 'To Date'}
              </span>
              <AppDateInput
                value={toDate || tillDate}
                onChange={(val) => (setToDate || setTillDate)?.(val)}
                placeholder="dd-mm-yyyy"
                inputClassName="h-9 text-xs"
              />
            </label>
          )}

          {activeFilters.includes('payHead') && payHeadOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Pay Head</span>
              <AppDropdown
                options={payHeadOptions}
                value={payHeadFilter}
                onChange={setPayHeadFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs font-medium"
              />
            </label>
          )}

          {activeFilters.includes('weeklyOff') && weeklyOffOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Weekly Off</span>
              <AppDropdown
                options={weeklyOffOptions}
                value={weeklyOffFilter}
                onChange={setWeeklyOffFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs font-medium"
              />
            </label>
          )}

          {activeFilters.includes('secondWeeklyOff') && secondWeeklyOffOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Second Weekly Off</span>
              <AppDropdown
                options={secondWeeklyOffOptions}
                value={secondWeeklyOffFilter}
                onChange={setSecondWeeklyOffFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs font-medium"
              />
            </label>
          )}

          {(activeFilters.includes('monthYear') || activeFilters.includes('month')) && monthOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Month</span>
              <AppDropdown
                options={monthOptions}
                value={monthFilter}
                onChange={setMonthFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs font-medium"
              />
            </label>
          )}

          {(activeFilters.includes('monthYear') || activeFilters.includes('year')) && yearOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Year</span>
              <AppDropdown
                options={yearOptions}
                value={yearFilter}
                onChange={setYearFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs font-medium"
              />
            </label>
          )}

          {(activeFilters.includes('empIdRange') || activeFilters.includes('empId')) && (
            <div className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Emp ID Range</span>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  value={fromEmpId !== undefined && fromEmpId !== null && fromEmpId !== '' ? fromEmpId : (empIdFilter || '')}
                  onChange={(e) => {
                    if (setFromEmpId) setFromEmpId(e.target.value);
                    else if (setEmpIdFilter) setEmpIdFilter(e.target.value);
                  }}
                  placeholder="From ID"
                  className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs font-mono text-on-surface outline-none placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20"
                />
                <input
                  type="number"
                  value={tillEmpId || ''}
                  onChange={(e) => setTillEmpId?.(e.target.value)}
                  placeholder="Till ID"
                  className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs font-mono text-on-surface outline-none placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          )}

          {(activeFilters.includes('amountRange') || activeFilters.includes('amount')) && (
            <div className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Amount Range (₹)</span>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={fromAmount !== undefined && fromAmount !== null && fromAmount !== '' ? fromAmount : (amountFilter || '')}
                  onChange={(e) => {
                    if (setFromAmount) setFromAmount(e.target.value);
                    else if (setAmountFilter) setAmountFilter(e.target.value);
                  }}
                  placeholder="Min ₹"
                  className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs font-mono text-on-surface outline-none placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20"
                />
                <input
                  type="number"
                  step="0.01"
                  value={tillAmount || ''}
                  onChange={(e) => setTillAmount?.(e.target.value)}
                  placeholder="Max ₹"
                  className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs font-mono text-on-surface outline-none placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          )}


          {activeFilters.includes('type') && typeOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Type</span>
              <AppDropdown
                options={typeOptions}
                value={typeFilter}
                onChange={setTypeFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('valClass') && valClassOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Val Class</span>
              <AppDropdown
                options={valClassOptions}
                value={valClassFilter}
                onChange={setValClassFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('stockType') && stockTypeOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Stock Type</span>
              <AppDropdown
                options={stockTypeOptions}
                value={stockTypeFilter}
                onChange={setStockTypeFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('course') && courseOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Course</span>
              <AppDropdown
                options={courseOptions}
                value={courseFilter}
                onChange={setCourseFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('school') && schoolOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">School / College</span>
              <AppDropdown
                options={schoolOptions}
                value={schoolFilter}
                onChange={setSchoolFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('faculty') && facultyOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Faculty</span>
              <AppDropdown
                options={facultyOptions}
                value={facultyFilter}
                onChange={setFacultyFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('category') && categoryOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Category</span>
              <AppDropdown
                options={categoryOptions}
                value={categoryFilter}
                onChange={setCategoryFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('academicYear') && academicYearOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">A.Year</span>
              <AppDropdown
                options={academicYearOptions}
                value={academicYearFilter}
                onChange={setAcademicYearFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('module') && moduleOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Module</span>
              <AppDropdown
                options={moduleOptions}
                value={moduleFilter}
                onChange={setModuleFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('type') && typeOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Type</span>
              <AppDropdown
                options={typeOptions}
                value={typeFilter}
                onChange={setTypeFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('period') && periodOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Period</span>
              <AppDropdown
                options={periodOptions}
                value={periodFilter}
                onChange={setPeriodFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('batch') && batchOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Batch</span>
              <AppDropdown
                options={batchOptions}
                value={batchFilter}
                onChange={setBatchFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('subject') && subjectOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Subject</span>
              <AppDropdown
                options={subjectOptions}
                value={subjectFilter}
                onChange={setSubjectFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('day') && dayOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Day</span>
              <AppDropdown
                options={dayOptions}
                value={dayFilter}
                onChange={setDayFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('project') && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Project</span>
              <AppCombobox
                fetchOptions={projectOptions.length > 0 ? undefined : handleFetchProject}
                options={projectOptions}
                value={projectFilter === 'All' || projectFilter === 'all' ? '' : projectFilter}
                onChange={(val) => setProjectFilter?.(val || 'All')}
                placeholder="All Projects"
                searchPlaceholder="Search project..."
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('task') && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Task</span>
              <AppCombobox
                fetchOptions={taskOptions.length > 0 ? undefined : handleFetchTask}
                options={taskOptions}
                value={taskFilter === 'All' || taskFilter === 'all' ? '' : taskFilter}
                onChange={(val) => setTaskFilter?.(val || 'All')}
                placeholder="All Tasks"
                searchPlaceholder="Search task..."
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('job') && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Job</span>
              <AppCombobox
                fetchOptions={jobOptions.length > 0 ? undefined : handleFetchJob}
                options={jobOptions}
                value={jobFilter === 'All' || jobFilter === 'all' ? '' : jobFilter}
                onChange={(val) => setJobFilter?.(val || 'All')}
                placeholder="All Jobs"
                searchPlaceholder="Search job..."
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('lead') && leadOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Lead / Manager</span>
              <AppDropdown
                options={leadOptions}
                value={leadFilter}
                onChange={setLeadFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('priority') && priorityOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Priority</span>
              <AppDropdown
                options={priorityOptions}
                value={priorityFilter}
                onChange={setPriorityFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('billingMode') && billingModeOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Billing Mode</span>
              <AppDropdown
                options={billingModeOptions}
                value={billingModeFilter}
                onChange={setBillingModeFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('slaState') && slaStateOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">SLA Status</span>
              <AppDropdown
                options={slaStateOptions}
                value={slaStateFilter}
                onChange={setSlaStateFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('overrun') && overrunOptions.length > 0 && (
            <label className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Efficiency / Overrun</span>
              <AppDropdown
                options={overrunOptions}
                value={overrunFilter}
                onChange={setOverrunFilter}
                triggerClassName="h-9 w-full bg-surface-container-lowest text-xs"
              />
            </label>
          )}

          {activeFilters.includes('deptStock') && (
            <div className="grid gap-1.5">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">Dept. Stock</span>
              <div className="flex items-center gap-2.5 h-9 shrink-0 select-none">
                <button
                  type="button"
                  onClick={() => setDeptStockFilter(!deptStockFilter)}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${deptStockFilter ? 'bg-primary' : 'bg-outline-variant'
                    }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-surface shadow-md ring-0 transition duration-200 ease-in-out ${deptStockFilter ? 'translate-x-4' : 'translate-x-0'
                      }`}
                  />
                </button>
                <span className="text-xs text-gray-600 font-medium select-none">
                  {deptStockFilter ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
